import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import Layout from '../components/layout/Layout';

// Styled components
const Container = styled.div`
  min-height: 100vh;
  background-color: white;
  overflow-x: hidden;
`;

// Navigation
const Navbar = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background-color: white;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const Logo = styled(Link)`
  font-size: 2rem;
  font-weight: bold;
  text-decoration: none;
  display: flex;
  align-items: center;
`;

const NavMenu = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled(Link)`
  text-decoration: none;
  color: #333;
  font-weight: 500;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    transition: width 0.3s;
  }
  
  &:hover:after {
    width: 100%;
  }
`;

const NavCTA = styled.div`
  display: flex;
  gap: 1rem;
`;

const SignInButton = styled(Link)`
  padding: 0.5rem 1rem;
  border: none;
  background: none;
  color: #333;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  font-family: 'DM Sans', sans-serif;
`;

const JoinPartnerButton = styled(Link)`
  background: linear-gradient(to right, #291759, #858CD3);
  color: white;
  border: none;
  border-radius: 9999px;
  height: 42px;
  padding: 0 1.5rem;
  font-size: 1rem;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

// Button component for the hero section
const Button = styled(Link)`
  background: linear-gradient(to right, #291759, #858CD3);
  color: white;
  border: none;
  border-radius: 9999px;
  height: 62px;
  padding: 0 2rem;
  font-size: 1.125rem;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

// Hero Section
const HeroSectionWrapper = styled.section`
  width: 100%;
  padding: 3rem 1rem 6rem;
  background: linear-gradient(to bottom right, #F2F5F8, #E8F0F5);
  margin-top: 0;

  @media (min-width: 768px) {
    padding: 6rem 1.5rem;
  }

  @media (min-width: 1024px) {
    padding: 8rem 1.5rem;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.5rem;
  max-width: 700px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: bold;
  letter-spacing: -0.025em;
  margin-bottom: 0.5rem;
  
  @media (min-width: 640px) {
    font-size: 4rem;
  }
  
  @media (min-width: 768px) {
    font-size: 4.75rem;
  }
`;

const HeroSubtitle = styled.p`
  color: #4B5563;
  max-width: 700px;
  margin: 0 auto;
  font-size: 1.125rem;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const HeroButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

// Feature Section
const FeatureSectionWrapper = styled.section`
  width: 100%;
  padding: 3.5rem;
  border-radius: 0.5rem;
  margin: 5rem auto;
  max-width: 1200px;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`;

const FeatureColumn = styled.div`
  flex: 1;
  position: relative;
  
  &::after {
    position: absolute;
    right: 0;
    top: 10%;
    bottom: 10%;
    width: 1px;
    background-color: ${({ theme }) => theme.colors.border};
    content: ${props => props.$hasDivider ? '""' : 'none'};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    &::after {
      display: none;
    }
  }
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const FeatureIcon = styled.div`
  width: 66px;
  height: 71px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeatureTitle = styled.h3`
  color: #2C2C2C;
  font-size: 1.5rem;
  font-weight: 500;
  font-family: sans-serif;
  text-align: center;
`;

// About Section
const AboutSection = styled.section`
  padding: 5rem 1rem;
  
  @media (min-width: 768px) {
    padding: 5rem 2rem;
  }
  
  @media (min-width: 1024px) {
    padding: 5rem 10rem;
  }
`;

const AboutContent = styled.div`
  max-width: 967px;
  margin: 0 auto;
  text-align: center;
`;

const AboutTag = styled.p`
  font-size: 1.125rem;
  font-weight: 500;
  color: #363230;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
`;

const AboutTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 500;
  color: #020120;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3.75rem;
  }
`;

const AboutText = styled.p`
  font-size: 1.25rem;
  color: #363230;
  margin-bottom: 2.5rem;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const AboutButton = styled.button`
  background: linear-gradient(to right, #291759, #858CD3);
  color: white;
  border: none;
  border-radius: 9999px;
  height: 62px;
  padding: 1rem;
  font-size: 1.2rem;
  text-transform: uppercase;
  cursor: pointer;
`;

// Products Section
const ProductsSection = styled.section`
  padding: 4rem 1rem;
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
  
  @media (min-width: 1024px) {
    padding: 4rem 10rem;
  }
`;

const ProductsHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 2.5rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const ProductsTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3.75rem;
  }
`;

const ProductsSubtitle = styled.p`
  font-size: 1.125rem;
  color: #102F32;
  line-height: 1.2;
  max-width: 313px;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const ProductCard = styled.div`
  border-radius: 1.5rem;
  overflow: hidden;
  height: 700px;
`;

const ProductImageContainer = styled.div`
  position: relative;
  height: 520px;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProductTag = styled.span`
  position: absolute;
  top: 2.5rem;
  left: 2.5rem;
  background-color: ${props => props.$bgColor || '#F4D374'};
  color: #102F32;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  border-radius: 9999px;
  padding: 0.5rem 1.25rem;
`;

const ProductInfo = styled.div`
  background-color: #EBEAEF;
  padding: 2.5rem;
  position: relative;
  border-radius: 0 0 1.5rem 1.5rem;
  height: 180px;
`;

const ProductTitle = styled.h3`
  font-size: 1.875rem;
  font-weight: 500;
  color: #102F32;
  margin-bottom: 0.5rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const ProductPrice = styled.p`
  font-size: 1.125rem;
  color: #102F32;
  opacity: 0.7;
  margin-bottom: 1rem;
`;

const ArrowButton = styled.div`
  position: absolute;
  right: 2.5rem;
  bottom: 2.5rem;
  width: 55px;
  height: 55px;
  border-radius: 9999px;
  background: linear-gradient(to bottom, #312164, #7B80C6);
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Plans Section
const PlansSection = styled.section`
  padding: 4rem 1rem;
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
  
  @media (min-width: 1024px) {
    padding: 4rem 10rem;
  }
`;

const PlansHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 2.5rem;
  text-align: center;
  
  @media (min-width: 768px) {
    flex-direction: row;
    text-align: left;
  }
`;

const PlansTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3.75rem;
  }
`;

const PlansSubtitle = styled.p`
  font-size: 1.125rem;
  color: #102F32;
  line-height: 1.2;
  max-width: 313px;
  margin: 0 auto;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
    margin: 0;
  }
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const PlanCard = styled.div`
  border-radius: 1.5rem;
  overflow: hidden;
  height: 700px;
  background: white;
  box-shadow: 0 15px 35px rgba(0,0,0,0.08);
  transition: all 0.4s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }
`;

const PlanImageContainer = styled.div`
  position: relative;
  height: 520px;
`;

const PlanImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlanTag = styled.span`
  position: absolute;
  top: 2.5rem;
  left: 2.5rem;
  background-color: ${props => props.$bgColor || '#F4D374'};
  color: #102F32;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  border-radius: 9999px;
  padding: 0.5rem 1.25rem;
`;

const PlanInfo = styled.div`
  background-color: #EBEAEF;
  padding: 2.5rem;
  position: relative;
  border-radius: 0 0 1.5rem 1.5rem;
  height: 180px;
`;

const PlanTitle = styled.h3`
  font-size: 1.875rem;
  font-weight: 500;
  color: #102F32;
  margin-bottom: 0.5rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const PlanPrice = styled.p`
  font-size: 1.125rem;
  color: #102F32;
  opacity: 0.7;
  margin-bottom: 1rem;
`;

const PlanArrowButton = styled.div`
  position: absolute;
  right: 2.5rem;
  bottom: 2.5rem;
  width: 55px;
  height: 55px;
  border-radius: 9999px;
  background: linear-gradient(to bottom, #312164, #7B80C6);
  display: flex;
  align-items: center;
  justify-content: center;
`;

// App Feature Section
const AppFeatureSection = styled.section`
  padding: 4rem 1rem;
  background-color: #E6E8FF;
  border-radius: 1.5rem;
  margin: 5rem auto;
  max-width: 1847px;
  position: relative;
  display: none; /* Hide on mobile by default */
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
    display: block; /* Show on tablets and larger devices */
  }
  
  @media (min-width: 1024px) {
    padding: 4rem 5rem;
  }
`;

const AppFeatureTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 500;
  margin-bottom: 2rem;
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3.75rem;
  }
`;

const AppFeatureText = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;
  max-width: 1186px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const PhoneMockupContainer = styled.div`
  height: 500px;
  position: relative;
  margin-bottom: 5rem;
`;

const PhoneFlexContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

const PhoneMockup = styled.div`
  width: ${props => props.$isCenter ? '300px' : '260px'};
  height: ${props => props.$isCenter ? '580px' : '500px'};
  background-color: white;
  border-radius: 36px;
  overflow: hidden;
  margin: 0 1rem;
  box-shadow: ${props => props.$isCenter ? '0 10px 25px rgba(0, 0, 0, 0.2)' : '0 10px 15px rgba(0, 0, 0, 0.1)'};
  z-index: ${props => props.$isCenter ? 10 : 1};
  transform: ${props => {
    if (props.$isLeft) return 'rotate(-3deg)';
    if (props.$isRight) return 'rotate(3deg)';
    return 'none';
  }};
`;

const PhoneImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StatsContainer = styled.div`
  // border: 1px solid #342567;
  padding: 2.5rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  text-align: center;
  position: relative;
  margin-top: 5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const StatColumn = styled.div`
  padding-bottom: 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const StatDivider = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  height: 100%;
`;

const VerticalSeparator = styled.div`
  height: 7rem;
  width: 2px;
  background-color: #8E9196;
`;

const DividerCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background-color: #342567;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
`;

const StatNumber = styled.div`
  font-size: 90px;
  font-weight: 500;
  color: #2C1B5D;
`;

const StatText = styled.p`
  font-size: 1.5rem;
  color: #5A5A5A;
`;

// Science Section
const ScienceSection = styled.section`
  padding: 5rem 1rem;
  margin: 3rem auto 6rem;
  max-width: 1400px;
  
  @media (min-width: 768px) {
    padding: 5rem 2rem;
  }
`;

const ScienceContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ScienceImageContainer = styled.div`
  border-radius: 1rem;
  overflow: hidden;
  height: 500px;
`;

const ScienceImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ScienceContentContainer = styled.div`
  padding: 1rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const ScienceTag = styled.p`
  font-size: 1.125rem;
  color: #333;
  margin-bottom: 0.5rem;
`;

const ScienceTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 2.5rem;
  
  @media (min-width: 768px) {
    font-size: 3.5rem;
  }
`;

const ScienceBenefits = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const ScienceBenefitItem = styled.div`
  padding: 1.5rem;
  background-color: #f9f9f9;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f0f0f0;
    transform: translateY(-5px);
  }
`;

const ScienceBenefitTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 500;
  color: #333;
`;

const ScienceButton = styled.button`
  background: linear-gradient(to right, #291759, #858CD3);
  color: white;
  border: none;
  border-radius: 9999px;
  height: 62px;
  padding: 0 2rem;
  font-size: 1.125rem;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

// Load spinner
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #f9fafb;
`;

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Spinner = styled.div`
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 3px solid ${props => props.theme.colors.primary};
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: #4B5563;
  font-size: 1rem;
`;

// Icons
const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(-45deg)' }}>
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const MicroscopeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#241254" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 18h8M3 22h18M14 22a7 7 0 1 0 0-14h-1M9 14h.01"/>
    <path d="M9 12a2 2 0 0 0 1.009-3.734 2 2 0 0 1 .841-3.201L14 4"/>
  </svg>
);

const HeartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#241254" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
  </svg>
);

const DumbbellIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#241254" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6.5 6.5 11 11M21 16.5a2.5 2.5 0 0 0-2.5-2.5 2.5 2.5 0 0 0-2.5-2.5 2.5 2.5 0 0 0-2.5-2.5 2.5 2.5 0 0 0-2.5 2.5 2.5 2.5 0 0 0-2.5 2.5 2.5 2.5 0 0 0-2.5 2.5A2.5 2.5 0 0 0 5.5 19 2.5 2.5 0 0 0 8 16.5"/>
  </svg>
);

const CircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#241254" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
  </svg>
);

const ZapIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#241254" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>
  </svg>
);

const LockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#241254" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

// Partner Section
const PartnerSection = styled.section`
  padding: 5rem 1rem;
  margin: 3rem auto 6rem;
  max-width: 1400px;
  
  @media (min-width: 768px) {
    padding: 5rem 2rem;
  }
`;

const PartnerContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const PartnerImageContainer = styled.div`
  border-radius: 1rem;
  overflow: hidden;
  height: 500px;
  
  @media (min-width: 1024px) {
    order: 1;
  }
`;

const PartnerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PartnerContentContainer = styled.div`
  padding: 1rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
  
  @media (min-width: 1024px) {
    order: 0;
  }
`;

const PartnerTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 2.5rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const PartnerFeatures = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 2.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const PartnerFeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PartnerCheckmark = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#291759" fillOpacity="0.2" />
    <path d="M8 12L11 15L16 9" stroke="#291759" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PartnerFeatureText = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
`;

const PartnerCallToAction = styled.div`
  margin-top: 2rem;
`;

const PartnerCTAButton = styled.button`
  background: linear-gradient(to right, #291759, #858CD3);
  color: white;
  border: none;
  border-radius: 9999px;
  height: 62px;
  padding: 0 2rem;
  font-size: 1.125rem;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  width: max-content;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 0.9;
  }
`;

// Testimonials Section
const TestimonialsSection = styled.section`
  padding: 5rem 1rem;
  margin: 3rem auto 6rem;
  max-width: 1400px;
  overflow: hidden;
  
  @media (min-width: 768px) {
    padding: 5rem 2rem;
  }
`;

const TestimonialsContainer = styled.div`
  text-align: center;
`;

const TestimonialsTag = styled.p`
  font-size: 1.125rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 1rem;
`;

const TestimonialsTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 4rem;
  }
`;

const TestimonialsSubtitle = styled.p`
  font-size: 1.25rem;
  color: #4B5563;
  max-width: 700px;
  margin: 0 auto 4rem;
`;

const TestimonialsSlider = styled.div`
  display: flex;
  gap: 1.5rem;
  overflow-x: hidden;
  width: 100%;
  position: relative;
  padding: 1rem 0;
  
  @media (min-width: 768px) {
    gap: 1rem;
  }
  
  & > div {
    flex-shrink: 0;
    min-width: 280px;
  }

  /* Create the continuous animation effect */
  & > div {
    animation: slideTestimonials 25s linear infinite;
  }
  
  @keyframes slideTestimonials {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(calc(-100% - 1rem));
    }
  }
  
  &:hover > div {
    animation-play-state: paused;
  }
`;

const TestimonialCard = styled.div`
  height: 400px;
  border-radius: 1rem;
  overflow: hidden;
  background-color: ${props => props.$isFirst ? 'transparent' : '#e8f0f5'};
  width: 280px;
  
  @media (min-width: 768px) {
    width: 320px;
  }
`;

const TestimonialBeforeAfter = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const BeforeAfterImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BeforeLabel = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-weight: 500;
`;

const AfterLabel = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-weight: 500;
`;

const TestimonialContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TestimonialText = styled.p`
  font-size: 1.125rem;
  line-height: 1.6;
  margin-bottom: auto;
  color: #333;
`;

const TestimonialAuthor = styled.p`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 1.5rem 0 0.5rem;
  color: #333;
`;

const TestimonialStars = styled.div`
  display: flex;
  gap: 0.25rem;
  color: #FFB800;
`;

const StarIcon = ({ filled }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const TestimonialImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// Main Homepage component
const Home = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [initialRender, setInitialRender] = useState(true);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // Complete loading when page is fully rendered
  useEffect(() => {
    if (document.readyState === 'complete') {
      setIsLoading(false);
    } else {
      window.addEventListener('load', () => setIsLoading(false));
      return () => window.removeEventListener('load', () => setIsLoading(false));
    }
  }, []);

  // Skip lazy loading for above-the-fold content on initial render
  useEffect(() => {
    if (initialRender) {
      const timer = setTimeout(() => setInitialRender(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [initialRender]);

  // Fetch plans for homepage display
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plansRef = collection(db, 'plans');
        const plansQuery = query(plansRef, orderBy("preference", "asc"));
        const plansSnap = await getDocs(plansQuery);
        const planList = [];

        plansSnap.forEach(doc => {
          const data = doc.data();
          // Only include featured plans
          if (data.featured) {
            planList.push({ id: doc.id, ...data });
          }
        });

        // Limit to 3 plans for homepage display
        setPlans(planList.slice(0, 3));
      } catch (error) {
        console.error("Error fetching plans for homepage:", error);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingContent>
          <Spinner />
          <LoadingText>Loading GeoDiet...</LoadingText>
        </LoadingContent>
      </LoadingContainer>
    );
  }

  return (
    <Layout>
      <Container>
        {/* Hero section and rest of the content */}
        <HeroSectionWrapper>
          <HeroContent>
            <HeroTitle>
              <span style={{ color: 'black' }}>Geo</span>
              <span style={{ color: '#291759' }}>Diet</span>
            </HeroTitle>
            <HeroSubtitle>
              Personalized nutrition and lifestyle recommendations based on your unique metabolic response.
            </HeroSubtitle>
            
            <HeroButtonGroup>
              <Button to="/auth/signup">Get Started</Button>
              <Button to="/about" variant="outline">Learn More</Button>
            </HeroButtonGroup>
          </HeroContent>
        </HeroSectionWrapper>
        
        {/* Features Section */}
        <FeatureSectionWrapper>
          <FeatureGrid>
            <FeatureColumn>
              <FeatureItem>
                <FeatureIcon>
                  <MicroscopeIcon />
                </FeatureIcon>
                <FeatureTitle>
                  Science-Backed<br/>Personalization
                </FeatureTitle>
              </FeatureItem>
            </FeatureColumn>
            
            <FeatureColumn $hasDivider>
              <FeatureItem>
                <FeatureIcon>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <HeartIcon />
                    <DumbbellIcon />
                  </div>
                </FeatureIcon>
                <FeatureTitle>
                  Integrated<br/>Health & Fitness
                </FeatureTitle>
              </FeatureItem>
            </FeatureColumn>
            
            <FeatureColumn $hasDivider>
              <FeatureItem>
                <FeatureIcon>
                  <div style={{ position: 'relative' }}>
                    <CircleIcon />
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                      <ZapIcon />
                    </div>
                  </div>
                </FeatureIcon>
                <FeatureTitle>
                  Sustainable &<br/>Measurable Results
                </FeatureTitle>
              </FeatureItem>
            </FeatureColumn>
            
            <FeatureColumn $hasDivider>
              <FeatureItem>
                <FeatureIcon>
                  <LockIcon />
                </FeatureIcon>
                <FeatureTitle>
                  Privacy &<br/>Control
                </FeatureTitle>
              </FeatureItem>
            </FeatureColumn>
          </FeatureGrid>
        </FeatureSectionWrapper>
        
        {/* Plans Section */}
        <PlansSection>
          <PlansHeader>
            <div>
              <PlansTitle>
                Our Featured Plans
              </PlansTitle>
            </div>
            <div>
              <PlansSubtitle>
                Personalized solutions for your health journey
              </PlansSubtitle>
            </div>
          </PlansHeader>
          
          {plansLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Loading plans...</p>
            </div>
          ) : (
            <PlansGrid>
              {plans.map(plan => (
                <PlanCard key={plan.id} onClick={() => window.location.href = `/customer/select-plan/${plan.id}`}>
                  <PlanImageContainer>
                    <PlanImage 
                      src={plan.imageUrl || "https://images.unsplash.com/photo-1493770348161-369560ae357d?auto=format&w=800&q=75"} 
                      alt={plan.name} 
                    />
                    <PlanTag $bgColor={plan.category === 'bundle' ? '#BED1AB' : plan.category === 'diet' ? '#A6B8B9' : '#F4D374'}>
                      {plan.category}
                    </PlanTag>
                  </PlanImageContainer>
                  <PlanInfo>
                    <PlanTitle>{plan.name}</PlanTitle>
                    <PlanPrice>${plan.price}</PlanPrice>
                    <PlanArrowButton>
                      <ArrowRightIcon />
                    </PlanArrowButton>
                  </PlanInfo>
                </PlanCard>
              ))}
            </PlansGrid>
          )}
        </PlansSection>
        
        {/* About Section */}
        <AboutSection>
          <AboutContent>
            <AboutTag>Let's DO GEODIET</AboutTag>
            <AboutTitle>
              Change the way you eat,<br/>feel, and live.
            </AboutTitle>
            <AboutText>
              Health isn't traditionally easy to optimize, so we've built an entirely new approach. 
              Whether you're balancing your nutrition, enhancing your fitness, or tracking real-time 
              health insights, GeoDiet makes it easier to take control and unlock your full potential.
            </AboutText>
            <AboutButton as={Link} to="/about">Know OUR STORY</AboutButton>
          </AboutContent>
        </AboutSection>
        
        {/* Products Section */}
        <ProductsSection>
          <ProductsHeader>
            <div>
              <ProductsTitle>
                Make your body<br/>work for you
              </ProductsTitle>
            </div>
            <div>
              <ProductsSubtitle>
                Solutions that work with<br/>your body, for results that stick.
              </ProductsSubtitle>
            </div>
          </ProductsHeader>
          
          <ProductsGrid>
            {/* Product Card 1 */}
            <ProductCard>
              <ProductImageContainer>
                <ProductImage 
                  src="https://images.unsplash.com/photo-1518495973542-4542c06a5843?auto=format&w=800&q=75" 
                  alt="Workout sessions" 
                />
                <ProductTag>Subscription</ProductTag>
              </ProductImageContainer>
              <ProductInfo>
                <ProductTitle>Workout sessions</ProductTitle>
                <ProductPrice>Starting at $225/month</ProductPrice>
                <ArrowButton>
                  <ArrowRightIcon />
                </ArrowButton>
              </ProductInfo>
            </ProductCard>
            
            {/* Product Card 2 */}
            <ProductCard>
              <ProductImageContainer>
                <ProductImage 
                  src="https://images.unsplash.com/photo-1500673922987-e212871fec22?auto=format&w=800&q=75" 
                  alt="GeoDiet" 
                />
                <ProductTag $bgColor="#BED1AB">certified GeoDiet partners</ProductTag>
              </ProductImageContainer>
              <ProductInfo>
                <ProductTitle>GeoDiet Access</ProductTitle>
                <ProductPrice>$0 out-of-pocket for most members</ProductPrice>
                <ArrowButton>
                  <ArrowRightIcon />
                </ArrowButton>
              </ProductInfo>
            </ProductCard>
            
            {/* Product Card 3 */}
            <ProductCard>
              <ProductImageContainer>
                <ProductImage 
                  src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&w=800&q=75" 
                  alt="CGM App" 
                />
                <ProductTag $bgColor="#A6B8B9">Healthy Diet</ProductTag>
              </ProductImageContainer>
              <ProductInfo>
                <ProductTitle>Healthy Diet</ProductTitle>
                <ProductPrice>Most healthy diets are available</ProductPrice>
                <ArrowButton>
                  <ArrowRightIcon />
                </ArrowButton>
              </ProductInfo>
            </ProductCard>
          </ProductsGrid>
        </ProductsSection>
        
        
        {/* Testimonials Section */}
        <TestimonialsSection>
          <TestimonialsContainer>
            <TestimonialsTag>Testimonials</TestimonialsTag>
            <TestimonialsTitle>Real Stories, Real Transformations</TestimonialsTitle>
            <TestimonialsSubtitle>Join 10000+ members transforming their health</TestimonialsSubtitle>
            
            <TestimonialsSlider>
              {/* Testimonial 1 - Before/After */}
              <TestimonialCard $isFirst>
                <TestimonialBeforeAfter>
                  <BeforeAfterImage 
                    src="https://images.unsplash.com/photo-1520116468816-95b69f847357?auto=format&w=800&q=75" 
                    alt="Before and after transformation" 
                  />
                  <BeforeLabel>Before</BeforeLabel>
                  <AfterLabel>After</AfterLabel>
                </TestimonialBeforeAfter>
              </TestimonialCard>
              
              {/* Testimonial 2 */}
              <TestimonialCard>
                <TestimonialContent>
                  <TestimonialText>
                    I knew I wanted to keep using the GeoDiet because when I didn't have it, I missed it! . . . the 12 months is like a yearly gym membership.
                  </TestimonialText>
                  <TestimonialAuthor>John Smith</TestimonialAuthor>
                  <TestimonialStars>
                    <StarIcon filled />
                    <StarIcon filled />
                    <StarIcon filled />
                    <StarIcon filled />
                    <StarIcon filled />
                  </TestimonialStars>
                </TestimonialContent>
              </TestimonialCard>
              
              {/* Testimonial 3 */}
              <TestimonialCard>
                <TestimonialImage 
                  src="https://images.unsplash.com/photo-1565958011703-44f9829ba187?auto=format&w=800&q=75" 
                  alt="Healthy cake with berries" 
                />
              </TestimonialCard>
              
              {/* Testimonial 4 */}
              <TestimonialCard>
                <TestimonialContent>
                  <TestimonialText>
                    The personalized nutrition plan changed my life! I lost 15lbs and have so much more energy throughout the day!
                  </TestimonialText>
                  <TestimonialAuthor>Maria Johnson</TestimonialAuthor>
                  <TestimonialStars>
                    <StarIcon filled />
                    <StarIcon filled />
                    <StarIcon filled />
                    <StarIcon filled />
                    <StarIcon filled />
                  </TestimonialStars>
                </TestimonialContent>
              </TestimonialCard>
              
              {/* Duplicate cards for continuous scrolling */}
              <TestimonialCard $isFirst>
                <TestimonialBeforeAfter>
                  <BeforeAfterImage 
                    src="https://images.unsplash.com/photo-1520116468816-95b69f847357?auto=format&w=800&q=75" 
                    alt="Before and after transformation" 
                  />
                  <BeforeLabel>Before</BeforeLabel>
                  <AfterLabel>After</AfterLabel>
                </TestimonialBeforeAfter>
              </TestimonialCard>
              
              <TestimonialCard>
                <TestimonialContent>
                  <TestimonialText>
                    I knew I wanted to keep using the CGM because when I didn't have it, I missed it! . . . the 12 months is like a yearly gym membership.
                  </TestimonialText>
                  <TestimonialAuthor>John Smith</TestimonialAuthor>
                  <TestimonialStars>
                    <StarIcon filled />
                    <StarIcon filled />
                    <StarIcon filled />
                    <StarIcon filled />
                    <StarIcon filled />
                  </TestimonialStars>
                </TestimonialContent>
              </TestimonialCard>
              
              <TestimonialCard>
                <TestimonialImage 
                  src="https://images.unsplash.com/photo-1565958011703-44f9829ba187?auto=format&w=800&q=75" 
                  alt="Healthy cake with berries" 
                />
              </TestimonialCard>
              
              <TestimonialCard>
                <TestimonialContent>
                  <TestimonialText>
                    The personalized nutrition plan changed my life! I lost 15lbs and have so much more energy throughout the day!
                  </TestimonialText>
                  <TestimonialAuthor>Maria Johnson</TestimonialAuthor>
                  <TestimonialStars>
                    <StarIcon filled />
                    <StarIcon filled />
                    <StarIcon filled />
                    <StarIcon filled />
                    <StarIcon filled />
                  </TestimonialStars>
                </TestimonialContent>
              </TestimonialCard>
            </TestimonialsSlider>
          </TestimonialsContainer>
        </TestimonialsSection>
      </Container>
    </Layout>
  );
};

export default Home; 